<script lang="ts">
  import { bookingState, isLoggedIn, activityLog } from "./helpers/BookingState";
  import { AuthManager } from "./helpers/AuthManager";
  import { refreshBooking } from "./helpers/BookingManager";
  import Form from "./Form.svelte";
  import Cars from "./Cars.svelte";
  import { onMount, onDestroy } from "svelte";
  import { cancelBooking } from "./helpers/BookingManager";

  let isMenuOpen = false;
  let bookingEndTimestamp: number | null = null;
  let bookingCheckInterval: number;
  let mapDisplayed: boolean = false;
  let address: string = "";
  let distance: number = 500;
  let autoBookEnabled: boolean = false;
  let closestCarEnabled: boolean = false;
  let language: string = "FR";
  let storageDistance: number = 0;
  let selectedModels: string[] = [];

  let countdownInterval: number;
  let extensionInstalled = false;
  let bookingRefreshInterval = null;

  function switchLanguage() {
    language == "FR" ? (language = "EN") : (language = "FR");
  }

  function displayMap(event) {
    if (!mapDisplayed) {
        mapDisplayed = true;
    }
    distance = event.detail.distance;
    address = event.detail.address;
    autoBookEnabled = event.detail.autoBookEnabled;
    closestCarEnabled = event.detail.closestCarEnabled ?? closestCarEnabled;
    selectedModels = event.detail.selectedModels || [];

    let localDistance = Number(localStorage.getItem("distance") || 0);

    if (distance < localDistance) {
      window.plausible("Lower Distance", {
        props: {
          difference: localDistance - distance,
        },
      });
    } else if (distance > localDistance) {
      window.plausible("Higher Distance", {
        props: {
          difference: distance - localDistance,
        },
      });
    }

    localStorage.setItem("address", address);
    localStorage.setItem("distance", distance);
    localStorage.setItem("autoBookEnabled", String(autoBookEnabled));
    localStorage.setItem("closestCarEnabled", String(closestCarEnabled));
    localStorage.setItem("selectedModels", JSON.stringify(selectedModels));
  }

  // Format time remaining in mm:ss format
  function formatTimeRemaining(ms: number): string {
    if (ms <= 0) return "00:00";
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // Get booking end timestamp
  function updateBookingEndTimestamp() {
    if ($bookingState.booking) {
      bookingEndTimestamp = $bookingState.booking.EndDateTimestamp || null;
    } else {
      bookingEndTimestamp = null;
    }
  }

  $: if ($bookingState.booking) {
    updateBookingEndTimestamp();
  } else {
    bookingEndTimestamp = null;
  }

  onMount(async () => {
    // Load Google Maps API
    // process.env.GOOGLE_MAPS_API_KEY will be replaced at build time by rollup
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    document.head.appendChild(script);

    address = localStorage.getItem("address");
    storageDistance = +localStorage.getItem("distance");
    distance = storageDistance ? storageDistance : distance;
    const storedAutoBook = localStorage.getItem("autoBookEnabled");
    if (storedAutoBook !== null) autoBookEnabled = storedAutoBook === 'true';
    const storedClosestCar = localStorage.getItem("closestCarEnabled");
    if (storedClosestCar !== null) closestCarEnabled = storedClosestCar === 'true';
    const storedModels = localStorage.getItem("selectedModels");
    if (storedModels) { try { selectedModels = JSON.parse(storedModels); } catch(e) {} }
    window.plausible =
      window.plausible ||
      function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };

    // Initialize the userscript and try to load tokens
    extensionInstalled = await AuthManager.waitForUserscript();

    if (extensionInstalled) {
      // Try loading tokens from userscript storage
      const hasTokens = await AuthManager.loadTokens();

      if (hasTokens) {
        // If we have tokens, refresh booking info
        await refreshBooking();
      } else {
        // If no tokens or expired tokens, try refresh token first
        await AuthManager.refreshAccessToken();
      }

      // Refresh every 20s with a booking, every 60s without
      let noBookingTicks = 0;
      bookingRefreshInterval = setInterval(async () => {
        if ($bookingState.booking) {
          noBookingTicks = 0;
          await refreshBooking();
        } else {
          noBookingTicks++;
          if (noBookingTicks >= 3) {
            noBookingTicks = 0;
            await refreshBooking();
          }
        }
      }, 20 * 1000);
    }

    // Set up interval to update the countdown timer
    bookingCheckInterval = setInterval(() => {
      if (bookingEndTimestamp) {
        // Force Svelte reactivity by creating a UI refresh
        // We need to create a new value to trigger reactivity
        const now = Date.now();
        if (document.querySelector('.countdown-timer')) {
          document.querySelector('.countdown-timer').textContent = formatTimeRemaining(bookingEndTimestamp - now);
        }
      }
    }, 1000);
  });

  onDestroy(() => {
    // Clean up all intervals on component destruction
    if (bookingCheckInterval) {
      clearInterval(bookingCheckInterval);
    }
    if (bookingRefreshInterval) {
      clearInterval(bookingRefreshInterval);
    }
    if (AuthManager.refreshTokenInterval) {
      clearInterval(AuthManager.refreshTokenInterval);
    }
  });
</script>

<main class="h-screen flex flex-col">
  <!-- Top Bar -->
  <header class="px-5 py-3 border-b flex justify-between items-center">
    <h1 class="text-commugreen text-xl">Flex Finder</h1>
    <div class="flex items-center gap-4">
      <!-- Login Status -->
      {#if extensionInstalled}
        {#if $isLoggedIn}
          <div class="relative">
            <button class="flex items-center gap-2" on:click={() => isMenuOpen = !isMenuOpen}>
              <span class="text-sm">📍 {$bookingState.booking ? `Car #${$bookingState.booking.CarNo}` : 'No booking'}</span>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
            {#if isMenuOpen}
            <div class="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg p-3 z-50">
              {#if $bookingState.booking}
                <p class="text-sm mb-2">Expires: {$bookingState.booking.EndDate}</p>
                {#if bookingEndTimestamp}
                <div class="mb-2 p-2 bg-gray-100 rounded-md">
                  <p class="text-sm font-medium">Time remaining:</p>
                  <p class="text-xl font-bold countdown-timer">{formatTimeRemaining(bookingEndTimestamp - Date.now())}</p>
                </div>
                {/if}
                <div class="flex gap-2">
                  <button class="text-sm px-2 py-1 rounded bg-commugreen text-white" on:click={refreshBooking}>Refresh</button>
                  <button class="text-sm px-2 py-1 rounded bg-red-500 text-white" on:click={cancelBooking}>Cancel</button>
                </div>
              {:else}
                <p class="text-sm">No active booking</p>
              {/if}
              <div class="mt-2 flex justify-between">
                <button class="text-sm text-red-500" on:click={() => { AuthManager.logout(); isMenuOpen = false; }}>
                  {language == "FR" ? "Déconnexion" : "Logout"}
                </button>
                <button class="text-sm text-gray-500" on:click={() => isMenuOpen = false}>Close</button>
              </div>
            </div>
            {/if}
          </div>
        {:else}
          <button class="text-sm text-commugreen" on:click={() => AuthManager.openCommunauto()}>
            Login
          </button>
        {/if}
      {/if}

      <button on:click={switchLanguage} class="text-sm">
        {language == "FR" ? "EN" : "FR"}
      </button>
    </div>
  </header>

  <!-- Main Content -->
  <div class="flex-1 flex overflow-hidden">
    <!-- Sidebar: always visible on desktop, hidden on mobile when map is shown -->
    <div class="sidebar border-r p-5 bg-gray-50 flex flex-col gap-4 overflow-y-auto"
      class:sidebar-hidden={mapDisplayed}
    >
      <Form {language} {address} {distance} {autoBookEnabled} {closestCarEnabled} {selectedModels} on:message={displayMap} />
      {#if $activityLog.length > 0}
        <div class="border-t pt-3">
          <p class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{language == 'FR' ? 'Activité' : 'Activity'}</p>
          <div class="space-y-1">
            {#each $activityLog as entry}
              <p class="text-xs text-gray-500">
                <span class="text-gray-300 font-mono mr-1">{String(entry.time.getHours()).padStart(2,'0')}:{String(entry.time.getMinutes()).padStart(2,'0')} –</span>
                {entry.message}
              </p>
            {/each}
          </div>
        </div>
      {/if}
    </div>

    <!-- Single Cars instance — avoids double API calls -->
    {#if mapDisplayed}
      <div class="flex-1 h-full relative">
        <!-- Mobile back button -->
        <button class="back-btn absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg px-4 py-2 flex items-center gap-2"
          on:click={() => mapDisplayed = false}>
          <svg class="w-4 h-4" viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
          {language == "FR" ? "Filtres" : "Filters"}
        </button>
        <Cars {language} {address} {distance} {autoBookEnabled} {closestCarEnabled} {selectedModels} />
      </div>
    {:else}
      <div class="placeholder flex-1 h-full flex items-center justify-center text-gray-500">
        {language == "FR"
          ? "Entrez une adresse pour commencer la recherche"
          : "Enter an address to start searching"}
      </div>
    {/if}
  </div>
</main>

<style>
  .sidebar {
    width: 100%;
    flex-shrink: 0;
  }
  .sidebar.sidebar-hidden {
    display: none;
  }
  /* Desktop: sidebar always visible at 25% width */
  @media (min-width: 768px) {
    .sidebar, .sidebar.sidebar-hidden {
      display: flex !important;
      width: 25%;
    }
    .back-btn {
      display: none;
    }
    .placeholder {
      display: flex;
    }
  }
</style>
